/* eslint-disable max-lines */

import { CustomDataAttrGtmClick } from '../../../features/google/customDataType';
import { DefaultProps } from '../../../types/DefaultProps';
import { ComponentProps as IconProps } from '../../Icon';

import styles from './ButtonBase.module.scss';

import type { FC, ReactNode, ComponentType, MouseEventHandler } from 'react';

import { assertNever } from '@/packages/nurse-senka-frontend-sdk';

type ButtonBaseProps = {
  width: 'default' | 'extend' | 'wide' | 'onlyIcon';
  link?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  disable?: boolean;
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};
// Primary Styled
// eslint-disable-next-line
const ButtonBasePrimary: FC<ButtonBaseProps> = ({
  width,
  link,
  children,
  target,
  disable,
  onClick,
}) => {
  switch (width) {
    case 'extend':
      return (
        <>
          {link ? (
            <a
              className={styles['primary-extend-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['primary-extend']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'wide':
      return (
        <>
          {link ? (
            <a
              className={styles['primary-wide-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['primary-wide']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'onlyIcon':
      return (
        <>
          {link ? (
            <a
              className={styles['primary-icon-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['primary-icon']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'default':
      return (
        <>
          {link ? (
            <a className={styles['primary-link']} href={link} target={target}>
              {children}
            </a>
          ) : (
            <button
              className={styles.primary}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    default:
      return assertNever(width);
  }
};

// Secondary Styled
// eslint-disable-next-line
const ButtonBaseSecondary: FC<ButtonBaseProps> = ({
  width,
  link,
  children,
  target,
  disable,
  onClick,
}) => {
  switch (width) {
    case 'extend':
      return (
        <>
          {link ? (
            <a
              className={styles['secondary-extend-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['secondary-extend']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'wide':
      return (
        <>
          {link ? (
            <a
              className={styles['secondary-wide-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['secondary-wide']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'onlyIcon':
      return (
        <>
          {link ? (
            <a
              className={styles['secondary-icon-link']}
              href={link}
              target={target}
            >
              {children}
            </a>
          ) : (
            <button
              className={styles['secondary-icon']}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    case 'default':
      return (
        <>
          {link ? (
            <a className={styles['secondary-link']} href={link} target={target}>
              {children}
            </a>
          ) : (
            <button
              className={styles.secondary}
              disabled={disable === true}
              onClick={onClick}
            >
              {children}
            </button>
          )}
        </>
      );
    default:
      return assertNever(width);
  }
};

export type ButtonProps = DefaultProps & {
  color: 'primary' | 'secondary';
  width: 'default' | 'extend' | 'wide' | 'onlyIcon';
  children?: ReactNode;
  link?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  customDataAttrGtmClick?: CustomDataAttrGtmClick;
  icon?: ComponentType<IconProps>;
  disable?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

export const ButtonBase: FC<ButtonProps> = ({
  children,
  color,
  link,
  width,
  target,
  icon: Icon,
  disable,
  onClick,
}) => {
  switch (color) {
    case 'primary':
      return (
        <ButtonBasePrimary
          link={link}
          target={target}
          width={width}
          disable={disable}
          onClick={onClick}
        >
          {children}
          {Icon ? <Icon size={18} /> : ''}
        </ButtonBasePrimary>
      );
    case 'secondary':
      return (
        <ButtonBaseSecondary
          link={link}
          target={target}
          width={width}
          disable={disable}
          onClick={onClick}
        >
          {children}
          {Icon ? <Icon size={18} /> : ''}
        </ButtonBaseSecondary>
      );
    default:
      return assertNever(color);
  }
};
